import type { Recipe } from '../types';

export const bratwurstLentilSoup: Recipe = {
  slug: 'bratwurst-lentil-soup',
  title: 'Bratwurst & Lentil Soup',
  meal: 'lunch',
  category: 'meal',
  types: ['soup'],
  cuisines: ['german'],
  traits: ['freezer-friendly', 'one-pot'],
  yield: '8 servings',
  activeTime: '30 min',
  totalTime: '1 hr 15 min',
  freezerLife: '4–6 months',
  difficulty: 1,

  summary:
    'Bulk bratwurst browned into a lentil and root-vegetable soup. One pot, freezes for months, and it thickens overnight into something closer to a stew.',

  flavorNotes:
    'This is German comfort food, not Italian — worth knowing, because the two look identical in the pot. Bratwurst seasoning is warm and slightly sweet: nutmeg, marjoram, a little coriander and ginger. It is not herby, and there is no fennel in it. Expect savory and rounded, almost creamy once the lentils start breaking down, with the tomato sitting in the background rather than out front. If it tastes flat when it is done, it needs salt — not more herbs. Add it a little at a time until the flavors suddenly snap into focus.',

  ingredients: [
    {
      id: 'bratwurst',
      name: 'Bratwurst, bulk',
      total: '1½ lb',
      prep: 'no casings — browns like ground beef',
      fromFreezer: true,
      uses: [{ step: 1, amount: '1½ lb' }],
    },
    {
      id: 'olive-oil',
      name: 'Olive oil',
      total: '2 tbsp',
      uses: [{ step: 2, amount: '2 tbsp', note: 'only if the sausage was lean' }],
    },
    {
      id: 'onion',
      name: 'Yellow onion',
      total: '1 large',
      prep: 'diced small, about ¼ inch',
      uses: [{ step: 2, amount: '1 large' }],
    },
    {
      id: 'carrots',
      name: 'Carrots',
      total: '4 medium',
      prep: 'diced small, about ¼ inch',
      uses: [{ step: 2, amount: '4 medium' }],
    },
    {
      id: 'celery',
      name: 'Celery',
      total: '3 stalks',
      prep: 'diced small, about ¼ inch',
      uses: [{ step: 2, amount: '3 stalks' }],
    },
    {
      id: 'garlic',
      name: 'Garlic',
      total: '6 cloves',
      prep: 'minced',
      uses: [{ step: 3, amount: '6 cloves' }],
    },
    {
      id: 'tomato-paste',
      name: 'Tomato paste',
      total: '2 tbsp',
      uses: [{ step: 3, amount: '2 tbsp' }],
    },
    {
      id: 'lentils',
      name: 'Brown lentils, dry',
      total: '2 cups',
      prep: 'rinsed in a colander',
      uses: [{ step: 4, amount: '2 cups' }],
    },
    {
      id: 'diced-tomatoes',
      name: 'Diced tomatoes',
      total: '1 can (28 oz)',
      prep: 'undrained',
      uses: [{ step: 4, amount: '1 can (28 oz)' }],
    },
    {
      id: 'broth',
      name: 'Chicken broth',
      total: '8 cups',
      prep: 'both 32 oz cartons',
      uses: [{ step: 4, amount: '8 cups' }],
    },
    {
      id: 'potatoes',
      name: 'Gold potatoes',
      total: '1½ lb',
      prep: 'cut into ½-inch cubes, skin on',
      uses: [{ step: 4, amount: '1½ lb' }],
    },
    {
      id: 'bay',
      name: 'Bay leaves',
      total: '2 leaves',
      uses: [{ step: 4, amount: '2 leaves', note: 'fished out at the end' }],
    },
    {
      id: 'thyme',
      name: 'Dried thyme',
      total: '1 tsp',
      uses: [{ step: 4, amount: '1 tsp' }],
    },
    {
      id: 'spinach',
      name: 'Baby spinach',
      total: '3 oz',
      prep: 'roughly chopped — about 3 big handfuls',
      uses: [{ step: 6, amount: '3 oz', note: 'wilts away to nothing' }],
    },
    {
      id: 'parmesan',
      name: 'BelGioioso grated Parmesan',
      total: 'about 1 oz',
      optional: true,
      optionalKind: 'garnish',
      optionalWhy:
        'Melts into the bowl and adds salt and body. The soup is finished and correct without it.',
      uses: [{ step: 7, amount: 'a spoonful per bowl', note: 'at the table' }],
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Brown the bratwurst',
      time: '10 min',
      ingredientIds: ['bratwurst'],
      body:
        'Put the bratwurst in a large dry pot over medium-high heat and break it up with a wooden spoon as it cooks, the same way you would ground beef. Keep breaking it up small — you want crumbles, not clumps. Cook until there is no pink left and some of it has browned, about 8 minutes. Scoop it out with a slotted spoon and set it aside, leaving the fat behind.',
      tip:
        'Brat seasoning runs fatty. Pour off all but about {2 tablespoons} of the fat — that stays in the pot to cook the vegetables and carries a lot of the flavor.',
    },
    {
      n: 2,
      title: 'Soften the vegetables',
      time: '8 min',
      ingredientIds: ['olive-oil', 'onion', 'carrots', 'celery'],
      body:
        'Into the reserved fat go the onion, carrots, and celery. If the sausage was lean and there is not much fat in the pot, add the olive oil. Cook over medium heat, stirring now and then, until the onion turns translucent and the carrots have lost their raw crunch, about 8 minutes. Do not rush this or brown them hard — you want them soft and sweet, not caramelized.',
    },
    {
      n: 3,
      title: 'Bloom the garlic and tomato paste',
      time: '2 min',
      critical: true,
      ingredientIds: ['garlic', 'tomato-paste'],
      body:
        'Add the garlic and cook 30 seconds, just until you can smell it — that is the bloom. Add the tomato paste and stir it around the bottom of the pot for a full minute — it will darken from bright red to brick and start sticking. That is exactly right.',
      tip:
        'Cooking the paste instead of just dumping it in is the single biggest flavor difference in this soup. Raw tomato paste tastes tinny and sharp; cooked, it turns deep and savory.',
    },
    {
      n: 4,
      title: 'Everything in the pot',
      time: '5 min',
      ingredientIds: ['lentils', 'diced-tomatoes', 'broth', 'potatoes', 'bay', 'thyme'],
      body:
        'Add the rinsed lentils, the whole can of diced tomatoes with its juice, the broth, the cubed waxy potatoes, bay leaves, and thyme. Scrape the bottom of the pot as it comes up to temperature to lift anything stuck on. Bring it to a boil.',
    },
    {
      n: 5,
      title: 'Simmer',
      time: '35–40 min',
      critical: true,
      ingredientIds: [],
      body:
        'Drop the heat to low, put the lid on with a small gap, and simmer for 35 to 40 minutes. It is done when the lentils are tender all the way through with no chalky center and the potatoes give easily to a fork. Stir once or twice along the way so nothing catches on the bottom.',
      tip:
        'Lentils are unpredictable — old ones can take 15 minutes longer than fresh. Taste one before you call it done rather than trusting the clock.',
    },
    {
      n: 6,
      title: 'Sausage back in, spinach through',
      time: '2 min',
      ingredientIds: ['spinach'],
      body:
        'Return the browned bratwurst to the pot along with any juice it released. Stir in the chopped spinach and let the residual heat wilt it down, about a minute. It collapses to almost nothing and disappears into the soup completely. Fish out the bay leaves.',
    },
    {
      n: 7,
      title: 'Season, then taste again',
      time: '3 min',
      ingredientIds: ['parmesan'],
      body:
        'Salt and pepper to taste, adding the salt gradually — lentil soups need more than feels reasonable, and there is a point where it goes from flat to right all at once. Serve with Parmesan at the table for anyone who wants it.',
      tip:
        'If it tastes muddy or heavy rather than flat, that is not a salt problem. A very small squeeze of lemon or splash of vinegar will lift it.',
    },
    {
      n: 8,
      title: 'Cool and pack for the freezer',
      time: '45 min, mostly waiting',
      ingredientIds: [],
      body:
        'Let the soup cool completely before it goes anywhere near the freezer — packing it hot forms ice crystals and turns the potatoes grainy. Spread it into a couple of shallow pans if you want it to cool faster. Portion into quart containers or quart freezer bags, leaving an inch of headspace because liquid expands as it freezes. Label with the name and date.',
    },
  ],

  freezing:
    'Cool completely, then portion into quart containers or bags with an inch of headspace. Keeps 4–6 months. Bags frozen flat stack better and thaw faster than tubs. The soup thickens considerably in the freezer — that is normal, not a mistake.',

  reheat: [
    {
      method: 'Stovetop',
      best: true,
      detail:
        'Thaw overnight in the fridge, then warm in a saucepan over medium-low, stirring occasionally, about 8 minutes. It will have thickened overnight — add a splash of broth or water until it loosens back to soup.',
    },
    {
      method: 'Microwave',
      detail:
        'Thaw overnight, then microwave in a covered bowl at 70% power in 2-minute rounds, stirring between rounds. Full power boils the edges before the middle is warm.',
    },
    {
      method: 'From frozen',
      detail:
        'Drop the frozen block into a saucepan with ¼ cup of water, cover, and set it over low heat. Break it up with a spoon as it loosens, about 20 minutes. Do not walk away from it.',
    },
  ],

  glossary: [
    {
      term: 'bloom',
      plain:
        'Cooking a paste or spice in hot fat for a minute before adding liquid. It changes raw, sharp flavors into deep, round ones. With tomato paste you can see it happen — the color goes from bright red to brick.',
    },
    {
      term: 'waxy potatoes',
      plain:
        'Gold and red potatoes, which hold their shape when cooked. Russets are starchy — they fall apart, and they turn grainy and mealy after freezing. Always gold for a soup that gets frozen.',
    },
    {
      term: 'headspace',
      plain:
        'The empty gap you leave at the top of a container. Liquid expands as it freezes, and without the gap the lid pops off or the container splits.',
    },
    {
      term: 'residual heat',
      plain:
        'The heat still in the pot after you turn the burner off. It is enough to wilt spinach or soften green onions without actually cooking them, which keeps their color and freshness.',
    },
  ],

  inspiration: [
    { label: 'Budget Bytes — Lentil and Sausage Stew', url: 'https://www.budgetbytes.com/lentil-sausage-stew/' },
    { label: 'Slender Kitchen — Sausage Lentil Soup', url: 'https://www.slenderkitchen.com/recipe/sausage-lentil-soup' },
  ],

  whereWeVeered:
    'Both sources build this on Italian sausage and lean herby — fennel, oregano, red pepper. We use the bratwurst from Liam\'s freezer instead, which moves the whole dish to a German profile (this is essentially Linsensuppe mit Würstchen) and means no fennel is needed at all. We also swap in gold potatoes because russets go grainy in the freezer, and finish with spinach that vanishes into the pot for a vegetable nobody has to notice.',
};
